import template from "./readingGuide.html";
import css from "./readingGuide.css";

// Global handles so the disable branch can detach whatever the enable branch attached.
const MOVE_KEY = "__cya11y__onScrollReadableGuide";   // desktop: mousemove follower
const RESIZE_KEY = "__cya11y__onResizeReadableGuide";  // legacy cleanup (older builds)
const TOUCH_KEY = "__cya11y__onTouchReadableGuide";    // touch: bag of listeners

/**
 * Touch / no-hover devices have no floating pointer for the mask to follow.
 * Detect by input capability (not screen width) so touch laptops and tablets
 * are classified correctly.
 */
function isNoHoverDevice(): boolean {
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

export default function readingGuide(enable = false) {
    let guide = document.querySelector(".cya11y-rg-container");

    if (enable) {
        if (!guide) {
            guide = document.createElement("div");
            guide.classList.add("cya11y-rg-container");
            // The mask is purely decorative — keep it out of the accessibility tree.
            guide.setAttribute("aria-hidden", "true");
            guide.innerHTML = `<style>${css}</style>${template}`;

            const rgTop: HTMLElement = guide.querySelector(".cya11y-rg-top");
            const rgBottom: HTMLElement = guide.querySelector(".cya11y-rg-bottom");

            // A fixed-height clear reading window centred on a viewport Y. Clamped
            // so it is always a proper strip and can never collapse to full black.
            const bandHeight = () => clamp(Math.round(window.innerHeight * 0.18), 90, 220);
            const placeBand = (centreY: number) => {
                const vh = window.innerHeight;
                const half = bandHeight() / 2;
                const cy = clamp(centreY, half, vh - half);
                rgTop.style.height = `${clamp(cy - half, 0, vh)}px`;
                rgBottom.style.height = `${clamp(vh - cy - half, 0, vh)}px`;
            };

            if (isNoHoverDevice()) {
                // Touch: the band starts centred and STAYS FIXED — it only moves when
                // the user taps, to the tapped line. We listen for the native `click`
                // event (not touchstart/move/end): the browser fires `click` only for a
                // genuine tap and never for a scroll/swipe, so scrolling can never nudge
                // the guide. A tap ANYWHERE repositions the band — including inside the
                // widget menu (no widget guard). placeBand() clamps the masks so they can
                // never overlap into a full-black screen, so no guard is needed for that.
                let centreY = window.innerHeight / 2;
                placeBand(centreY);

                // Clicks from the widget's shadow DOM still bubble to `document`
                // (composed), and e.clientY is the viewport tap position regardless of
                // shadow retargeting — so a tap on the menu repositions the guide too.
                const onClick = (e: MouseEvent) => {
                    centreY = e.clientY;
                    placeBand(centreY);
                };

                const onResize = () => placeBand(centreY);

                document.addEventListener("click", onClick, { passive: true });
                window.addEventListener("resize", onResize, { passive: true });
                (window as any)[TOUCH_KEY] = { onClick, onResize };
            } else {
                // Desktop / mouse: the clear band follows the pointer's vertical
                // position, clamped so a stray value can never overflow the viewport.
                const clearAbove = 20;
                const clearBelow = 100;
                // Remember the last pointer Y so the band can be re-placed on resize.
                let lastY = window.innerHeight / 2;
                const place = (y: number) => {
                    const vh = window.innerHeight;
                    lastY = y;
                    // Clamped against the CURRENT viewport height every time, so the
                    // two masks can never sum past the viewport and overlap.
                    rgTop.style.height = `${clamp(y - clearAbove, 0, vh)}px`;
                    rgBottom.style.height = `${clamp(vh - y - clearBelow, 0, vh)}px`;
                };
                place(lastY); // valid centred band immediately, before the first move

                (window as any)[MOVE_KEY] = (event: MouseEvent) => place(event.clientY);
                document.addEventListener("mousemove", (window as any)[MOVE_KEY], { passive: true });

                // Re-place on viewport change (desktop↔mobile resize, orientation,
                // mobile URL-bar show/hide) so a stale band never collapses to a
                // full-black screen while waiting for the next mouse move.
                (window as any)[RESIZE_KEY] = () =>
                    place(clamp(lastY, 0, window.innerHeight));
                window.addEventListener("resize", (window as any)[RESIZE_KEY], { passive: true });
            }

            document.body.appendChild(guide);
        }
    } else {
        guide?.remove();

        if ((window as any)[MOVE_KEY]) {
            document.removeEventListener("mousemove", (window as any)[MOVE_KEY]);
            delete (window as any)[MOVE_KEY];
        }
        const touch = (window as any)[TOUCH_KEY];
        if (touch) {
            document.removeEventListener("click", touch.onClick);
            window.removeEventListener("resize", touch.onResize);
            delete (window as any)[TOUCH_KEY];
        }
        if ((window as any)[RESIZE_KEY]) {
            window.removeEventListener("resize", (window as any)[RESIZE_KEY]);
            delete (window as any)[RESIZE_KEY];
        }
    }
}
